import React, { useState, useRef, useEffect } from "react";
import { FieldArray, Form, Formik } from "formik";
import {
  FormFeedback,
  FormGroup,
  Input,
  Popover,
  PopoverBody,
  PopoverHeader,
  Button,
  Label,
} from "reactstrap";
import {
  faAngleLeft,
  faCheck,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextInput } from "../../../components/common/FormsInput";
import PageHeader from "../../../components/common/PageHeader";
import "./index.scss";
import { userSchema, userInit } from "./schema";
import { roleItems } from "../../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import LocationSelectBox from "../../../components/common/FormsInput/LocationSelectBox";
import {
  getAllPermissions,
  getAllRoles,
} from "../../../context/actions/rolesAndPermission";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  get,
  add,
  update,
  changeStatus,
} from "../../../context/actions/operator";

const MySwal = withReactContent(Swal);

const AddUser = () => {
  const dispatch = useDispatch();
  const imageRef = useRef(null);
  const history = useHistory();
  const [payload, setPayload] = useState(userInit);
  const [permission, setPermission] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [options, setOptions] = useState([]);
  const [roledata, setRoledata] = useState("");
  const [selectAllChecked, setSelectedAllChecked] = useState(false);
  const [removeDisabled, setRemoveDisabled] = useState(false);
  const [imageSrc, setImageSrc] = useState();
  const [imageSelected, setImage] = useState();
  const [activePopover, setActivePopover] = useState(null);
  const [permissionsChecked, setPermissionsChecked] = useState([]);
  const [workGroupRequired, setWorkGroupRequired] = useState(true);
  const [locationSelected, setLocationSelected] = useState();

  const toggle = (id) => {
    setActivePopover(activePopover === id ? null : id);
  };

  const handleDocumentClick = (event) => {
    // check if the clicked element is inside the popover or the popover button
    const isInsidePopover = event.target.closest(".popover") !== null;
    const isPopoverButton = event.target.closest(".help-btn") !== null;

    // if the clicked element is outside the popover and the popover button is not clicked, close the popover
    if (!isInsidePopover && !isPopoverButton) {
      setActivePopover(null);
    }
  };

  useEffect(() => {
    document.body.addEventListener("click", handleDocumentClick);

    // cleanup functions to remove the event listeners when the component unmounts
    return () => {
      document.body.removeEventListener("click", handleDocumentClick);
    };
  }, [activePopover]);

  const handleRemoveClick = () => {
    setImageSrc(null);
    imageRef.current.value = "";
    setImage(null);
    toast.success("Image Removed");
  };

  useEffect(() => {
    if (imageSrc) {
      setRemoveDisabled(false);
    } else {
      setRemoveDisabled(true);
    }
  }, [imageSrc]);

  let locations = useSelector((state) => state?.user?.locations || []);

  // Get All The Permissions
  const permissions = () => {
    setShowLoader(true);
    dispatch(
      getAllPermissions({ type: "business" }, (res) => {
        setPermission(res.docs);
        setShowLoader(false);
      })
    );
  };

  // Get and set All Roles for Roles Dropdown
  const roles = () => {
    setShowLoader(true);
    dispatch(
      getAllRoles({}, (res) => {
        const filteredRoles = res.filter(
          (role) => !role?.roleName?.toLowerCase().startsWith("trained")
        );

        setRoledata(filteredRoles);
        setOptions(
          filteredRoles.map((role) => ({
            label: role?.roleName,
            value: role?._id,
          }))
        );
        setShowLoader(false);
      })
    );
  };

  useEffect(() => {
    permissions();
    roles();
    setPayload({
      ...userInit,
    });
  }, []);

  // Handle Checkboxes Change
  function handleCheckboxChange(event) {
    const checkboxValue = event.target.value;

    setPayload((previousState) => {
      const updatedPermissions = previousState?.permissions?.includes(
        checkboxValue
      )
        ? previousState?.permissions.filter(
            (permission) => permission !== checkboxValue
          )
        : [...previousState.permissions, checkboxValue];

      if (updatedPermissions?.length === permission?.length) {
        setSelectedAllChecked(true);
      } else {
        setSelectedAllChecked(false);
      }
      return {
        ...previousState,
        permissions: updatedPermissions,
      };
    });
  }

  // Function to Select All Checkboxes in Particular Role
  const selectAllCheckBoxes = (event, permissions) => {
    if (selectAllChecked) {
      setPayload((previousState) => ({
        ...previousState,
        permissions: [], // Empty the payload.permissions array
      }));
      setSelectedAllChecked(false);
    } else {
      let updatedPermissions = [...payload.permissions]; // Create a copy of existing permissions

      permission.forEach((permission) => {
        if (!updatedPermissions.includes(permission._id)) {
          updatedPermissions.push(permission._id); // Add the permission to the copy if it doesn't exist
        }
      });

      setPayload((previousState) => ({
        ...previousState,
        permissions: updatedPermissions, // Update payload.permissions with the updated copy
      }));
      setSelectedAllChecked(true);
    }
  };

  function validateRole(role) {
    // Check if the organizations object is empty
    if (role?.role == null || role?.role == "") {
      toast.error("Please Select Role");
      return false;
    } else if (role?.permissions == null || role?.permissions == "") {
      toast.error("Please Select Atleast One Permission");
      return false;
    }
    // If all of the checks pass, return true
    return true;
  }

  // Submit Form To Backend To add User
  const submitForm = (params) => {
    params.roles = { role: payload?.role, permissions: payload?.permissions };
    params.logo = imageSelected;
    params.location = locationSelected;
    if (
      !params?.location &&
      !options
        ?.find((label) => label?.value == params?.roles?.role)
        ?.label?.toLowerCase()
        ?.includes("admin")
    ) {
      toast.error("Workgroup need to be assigned for this role");
    } else {
      if (validateRole(params?.roles)) {
        setDisabled(true);
        if (params?.location == "") {
          delete params.location;
        }
        const formData = new FormData();
        for (const [key, value] of Object.entries(params)) {
          if (key === "logo" && value) {
            formData.append(key, value, value.name);
          } else {
            formData.append(key, value);
          }
        }
        formData.set("roles", JSON.stringify(params.roles));
        formData.set("role", roleItems[1]);
        dispatch(
          add(formData, (res) => {
            setDisabled(false);
            if (res) {
              history.push("/users");
            }
          })
        );
      }
    }
  };
  return (
    <>
      <div className="page-wrapper">
        <PageHeader className="with-btn">
          <div
            style={{ cursor: "pointer" }}
            className="left-col d-inline-flex align-items-center"
            onClick={() => {
              history.goBack();
            }}
          >
            <Button color="link" className="backBtn">
              {" "}
              <FontAwesomeIcon icon={faAngleLeft} />
            </Button>
            Add User
          </div>
        </PageHeader>
        <Formik
          initialValues={payload}
          validationSchema={userSchema}
          onSubmit={submitForm}
        >
          {(formik) => (
            <Form autoComplete="off">
              <div className="inner-wrapper position-relative add-user-page role-permission-page">
                <div className="row-block d-flex">
                  <div className="left-col">
                    <div className="user-block d-inline-flex flex-column align-items-center">
                      <div className="pic-cell rounded-circle position-relative overflow-hidden">
                        <img
                          className="img-fluid rounded-circle w-100 h-100"
                          src={
                            imageSrc
                              ? imageSrc
                              : require("../../../assets/img/useravatar.jpg")
                          }
                          alt="User"
                        />
                        <span className="upload-cell position-absolute d-inline-flex align-items-center justify-content-center">
                          <Input
                            innerRef={imageRef}
                            type="file"
                            className="position-absolute"
                            accept="image/png, image/jpg, image/jpeg, image/svg"
                            name="image"
                            onChange={(event) => {
                              const file = event.target.files[0];
                              if (file && file.type.startsWith("image/")) {
                                const reader = new FileReader();
                                reader.onload = (e) => {
                                  setImageSrc(e.target.result);
                                };
                                reader.readAsDataURL(file);
                                setImage(file);
                              } else {
                                toast.error("Only Images Are Allowed");
                                event.target.value = null; // Reset the file input value
                              }
                            }}
                          />
                          <span className="inner text-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              enable-background="new 0 0 24 24"
                              height="24px"
                              viewBox="0 0 24 24"
                              width="24px"
                              fill="#000000"
                            >
                              <rect fill="none" height="24" width="24" />
                              <path d="M3,4V1h2v3h3v2H5v3H3V6H0V4H3z M6,10V7h3V4h7l1.83,2H21c1.1,0,2,0.9,2,2v12c0,1.1-0.9,2-2,2H5c-1.1,0-2-0.9-2-2V10H6z M13,19c2.76,0,5-2.24,5-5s-2.24-5-5-5s-5,2.24-5,5S10.24,19,13,19z M9.8,14c0,1.77,1.43,3.2,3.2,3.2s3.2-1.43,3.2-3.2 s-1.43-3.2-3.2-3.2S9.8,12.23,9.8,14z" />
                            </svg>
                            <span className="d-block">Add Photo</span>
                          </span>
                        </span>
                      </div>
                      <div className="btn-cell">
                        <button
                          type="button"
                          className="btn btn-danger"
                          disabled={removeDisabled}
                          onClick={() => {
                            MySwal.fire({
                              title: "Are you sure?",
                              text: "Do you want to remove this image",
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonColor: "#3085d6",
                              cancelButtonColor: "#d33",
                              confirmButtonText: "Yes",
                            }).then((result) => {
                              if (result.isConfirmed) {
                                handleRemoveClick();
                              }
                            });
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="right-col flex-fill">
                    <div className="row">
                      <div className="col-lg-4 col-md-6">
                        <TextInput
                          type="text"
                          name="fname"
                          id="firstName"
                          value={formik?.values?.fname}
                          onChange={formik.handleChange}
                          placeholder="First Name"
                          label="First Name"
                        />
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <TextInput
                          type="text"
                          name="lname"
                          id="lastName"
                          value={formik?.values?.lname}
                          onChange={formik.handleChange}
                          isRequired={true}
                          placeholder="Last Name"
                          label="Last Name"
                        />
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <TextInput
                          type="email"
                          name="email"
                          id="superadminmail"
                          value={formik?.values?.email}
                          onChange={formik.handleChange}
                          isRequired={true}
                          placeholder="Email"
                          label="Email"
                        />
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <TextInput
                          type="phone"
                          name="phone"
                          value={formik?.values?.phone}
                          onChange={(event) => {
                            const inputPhoneNumber = event.target.value.replace(
                              /\D/g,
                              ""
                            );
                            let formattedPhoneNumber = "";

                            if (inputPhoneNumber.length < 4) {
                              formattedPhoneNumber = `(${inputPhoneNumber}`;
                            } else if (inputPhoneNumber.length < 7) {
                              formattedPhoneNumber = `(${inputPhoneNumber.slice(
                                0,
                                3
                              )}) ${inputPhoneNumber.slice(3)}`;
                            } else {
                              formattedPhoneNumber = `(${inputPhoneNumber.slice(
                                0,
                                3
                              )}) ${inputPhoneNumber.slice(
                                3,
                                6
                              )}-${inputPhoneNumber.slice(6, 10)}`;
                            }

                            formik.setFieldValue("phone", formattedPhoneNumber);
                            formik.setFieldTouched("phone", true);
                          }}
                          isRequired={true}
                          id="phone"
                          placeholder="Phone"
                          label="Phone"
                        />
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <LocationSelectBox
                          placeholder="Select Work Group"
                          name="location"
                          value={formik?.values?.location}
                          onChange={(e) => {
                            formik.handleChange(e);
                            formik.setFieldValue("location", e?.target?.value);
                            setLocationSelected(e?.target?.value)
                          }}
                          isRequired={workGroupRequired}
                          label="Work Group"
                          options={locations}
                        />
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <Label className="d-inline-flex align-items-center checkBoxCell"></Label>
                        <div
                          className={`input-cell position-relative position-relative-custom`}
                        >
                          <Input
                            type="checkbox"
                            name="purchase_advice"
                            defaultChecked={formik?.values?.purchase_advice}
                            onChange={(e) => {
                              formik.setFieldValue(
                                "purchase_advice",
                                e.target.checked
                              );
                            }}
                          />
                          <span className="txt">
                            &nbsp; Purchase Advice Upload Allowed
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="options-section-rw">
                      {/* <div className="head d-flex align-items-center">
                        <div className="left-col">
                          <h2>Organization, Roles & Permissions</h2>
                        </div>
                      </div> */}
                      <div className="row">
                        <div className="col-sm-12">
                          <div className="permission-form-wrapper">
                            <div className="mt-2 mb-2">
                              <div className="permission-form position-relative delete-section">
                                <div
                                  className="head d-flex align-items-center"
                                  style={{ minHeight: "50px" }}
                                >
                                  <div className="left-col">
                                    <h2>Roles & Permissions</h2>
                                  </div>
                                </div>

                                <div className="inner roles-permissions">
                                  <div className="permission-form-block-rw">
                                    <div className="permission-form-block">
                                      <div className="head d-flex align-items-center">
                                        <div className="left-col">
                                          <div className="role-input-cell">
                                            <FormGroup>
                                              <select
                                                className="form-control form-select"
                                                onChange={(selectedValue) => {
                                                  setWorkGroupRequired(
                                                    !options
                                                      ?.find(
                                                        (label) =>
                                                          label?.value ==
                                                          selectedValue?.target
                                                            ?.value
                                                      )
                                                      ?.label?.toLowerCase()
                                                      ?.includes("admin")
                                                  );
                                                  formik.handleChange(
                                                    selectedValue
                                                  );
                                                  formik.setFieldValue(
                                                    "roles",
                                                    selectedValue?.target?.value
                                                  );
                                                  const selectedRole =
                                                    roledata.find(
                                                      (role) =>
                                                        role._id ===
                                                        selectedValue?.target
                                                          ?.value
                                                    );
                                                  var permissionsId = [];
                                                  selectedRole.permissions.forEach(
                                                    (id) => {
                                                      if (
                                                        permission?.some(
                                                          (p) => p?._id == id
                                                        )
                                                      ) {
                                                        permissionsId.push(id);
                                                      }
                                                    }
                                                  );
                                                  setPayload(
                                                    (previousState) => ({
                                                      ...previousState,
                                                      role: selectedRole._id, // Set the value directly to payload.role
                                                      permissions:
                                                        permissionsId, // Update permissions as well if needed
                                                    })
                                                  );

                                                  if (
                                                    permission?.length ===
                                                    permissionsId?.length
                                                  ) {
                                                    setSelectedAllChecked(true);
                                                  } else {
                                                    setSelectedAllChecked(
                                                      false
                                                    );
                                                  }
                                                }}
                                              >
                                                <option disabled selected>
                                                  Select Role
                                                </option>
                                                {options.map((option) => (
                                                  <option value={option?.value}>
                                                    {option?.label}
                                                  </option>
                                                ))}
                                              </select>
                                            </FormGroup>
                                          </div>
                                        </div>
                                      </div>
                                      <FieldArray
                                        name="permissions"
                                        render={() => {
                                          return (
                                            <div>
                                              <div>
                                                <div className="form-heading">
                                                  <label className="checkbox-blc position-relative d-inline-flex align-items-center">
                                                    <Input
                                                      type="checkbox"
                                                      className="position-absolute"
                                                      checked={selectAllChecked}
                                                      onChange={(event) => {
                                                        selectAllCheckBoxes(
                                                          event,
                                                          permission
                                                        );
                                                      }}
                                                    />
                                                    <span className="icon-box d-inline-flex align-items-center justify-content-center">
                                                      <span className="icon d-inline-flex">
                                                        <FontAwesomeIcon
                                                          icon={faCheck}
                                                        />
                                                      </span>
                                                    </span>
                                                    <span className="txt">
                                                      Select All
                                                    </span>
                                                  </label>
                                                </div>
                                                <ul className="options-block d-flex flex-wrap list-unstyled">
                                                  {permission?.map(
                                                    (
                                                      permission,
                                                      permissionindex
                                                    ) => (
                                                      <li key={permission._id}>
                                                        <label className="checkbox-blc position-relative d-inline-flex align-items-center">
                                                          <Input
                                                            type="checkbox"
                                                            className="position-absolute"
                                                            name={
                                                              permission._id
                                                            }
                                                            value={
                                                              permission._id
                                                            }
                                                            checked={payload?.permissions.includes(
                                                              permission._id
                                                            )}
                                                            onChange={(
                                                              event
                                                            ) => {
                                                              handleCheckboxChange(
                                                                event
                                                              );
                                                            }}
                                                          />

                                                          <span className="icon-box d-inline-flex align-items-center justify-content-center">
                                                            <span className="icon d-inline-flex">
                                                              <FontAwesomeIcon
                                                                icon={faCheck}
                                                              />
                                                            </span>
                                                          </span>
                                                          <span className="txt">
                                                            {
                                                              permission.permissionName
                                                            }
                                                          </span>
                                                          <div className="help-popover-blc">
                                                            <Button
                                                              color="link"
                                                              id={`PopoverOrganization${permission._id}`}
                                                              type="button"
                                                              className="help-btn p-0"
                                                              onClick={() =>
                                                                toggle(
                                                                  `PopoverOrganization${permission._id}`
                                                                )
                                                              }
                                                            >
                                                              <FontAwesomeIcon
                                                                icon={
                                                                  faQuestion
                                                                }
                                                              />
                                                            </Button>
                                                            <Popover
                                                              placement="right"
                                                              isOpen={
                                                                activePopover ===
                                                                `PopoverOrganization${permission._id}`
                                                              }
                                                              target={`PopoverOrganization${permission._id}`}
                                                              toggle={() =>
                                                                toggle(
                                                                  `PopoverOrganization${permission._id}`
                                                                )
                                                              }
                                                            >
                                                              <PopoverHeader>
                                                                {
                                                                  permission.permissionName
                                                                }{" "}
                                                                Info
                                                              </PopoverHeader>
                                                              <PopoverBody>
                                                                {
                                                                  permission.description
                                                                }
                                                              </PopoverBody>
                                                            </Popover>
                                                          </div>
                                                        </label>
                                                      </li>
                                                    )
                                                  )}
                                                </ul>
                                              </div>
                                            </div>
                                          );
                                        }}
                                      ></FieldArray>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-sm-12">
                        <div className="col-sm-12 form-btns-rw">
                          <Button
                            color="link"
                            type="button"
                            onClick={() => {
                              history.goBack();
                            }}
                          >
                            Cancel
                          </Button>
                          <Button color="primary" type="submit">
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};
export default AddUser;
